/**
 * Optimizer Control Center — inline interval editor (presets + custom)
 * For Media Devoted | Created By Ciro Autiero
 */

import { useState, type FC } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Pencil, X } from 'lucide-react';

import { Button, Input } from '@/components/ui';
import { upsertConfig } from '@/features/flow-optimizer/services/flow-optimizer-api';
import { optimizerCenterKeys } from '@/features/optimizer-center/hooks/useOptimizerConfigs';
import type { OptimizerRow } from '@/features/optimizer-center/optimizer-center.types';
import { formatIntervalMin } from '@/features/optimizer-center/utils/next-run-info';
import { getApiErrorMessage } from '@/lib/utils';
import { useToast } from '@/providers/ToastProvider';

export const INTERVAL_PRESETS = [15, 30, 60, 180, 360, 720, 1440] as const;
const CUSTOM = 'custom';

export const IntervalCell: FC<{ row: OptimizerRow; canEdit: boolean }> = ({
  row,
  canEdit,
}) => {
  const [editing, setEditing] = useState(false);
  const [choice, setChoice] = useState<string>(String(row.intervalMin));
  const [customH, setCustomH] = useState<string>(
    String(Math.floor(row.intervalMin / 60)),
  );
  const [customM, setCustomM] = useState<string>(String(row.intervalMin % 60));
  const queryClient = useQueryClient();
  const toast = useToast();

  const save = useMutation({
    mutationFn: (intervalMin: number) =>
      upsertConfig(row.campaignId, { flowId: row.flowId, intervalMin }, row.provider),
    onSuccess: () => {
      toast.success('Interval updated');
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: optimizerCenterKeys.all });
    },
    onError: (err) =>
      toast.error('Failed to update interval', getApiErrorMessage(err)),
  });

  if (!editing) {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs tabular-nums">
        {formatIntervalMin(row.intervalMin)}
        {canEdit && (
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 text-muted-foreground"
            aria-label="Edit interval"
            onClick={() => setEditing(true)}
          >
            <Pencil className="h-3 w-3" />
          </Button>
        )}
      </span>
    );
  }

  const customTotal =
    (Number.parseInt(customH || '0', 10) || 0) * 60 +
    (Number.parseInt(customM || '0', 10) || 0);
  const minutes = choice === CUSTOM ? customTotal : Number.parseInt(choice, 10);
  const valid = Number.isFinite(minutes) && minutes >= 1;

  return (
    <span className="inline-flex items-center gap-1">
      <select
        aria-label="Interval preset"
        className="h-7 rounded-md border border-input bg-background px-1 text-xs"
        value={choice}
        onChange={(e) => setChoice(e.target.value)}
      >
        {INTERVAL_PRESETS.map((m) => (
          <option key={m} value={m}>
            {formatIntervalMin(m)}
          </option>
        ))}
        <option value={CUSTOM}>Custom…</option>
      </select>
      {choice === CUSTOM && (
        <span className="inline-flex items-center gap-1">
          <Input
            aria-label="Custom hours"
            inputMode="numeric"
            className="h-7 w-12 text-xs"
            value={customH}
            onChange={(e) => setCustomH(e.target.value.replace(/\D/g, ''))}
            placeholder="0"
          />
          <span className="text-xs text-muted-foreground">h</span>
          <Input
            aria-label="Custom minutes"
            inputMode="numeric"
            className="h-7 w-12 text-xs"
            value={customM}
            onChange={(e) => setCustomM(e.target.value.replace(/\D/g, ''))}
            placeholder="0"
          />
          <span className="text-xs text-muted-foreground">m</span>
        </span>
      )}
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 text-success"
        aria-label="Save interval"
        disabled={!valid || save.isPending}
        onClick={() => save.mutate(minutes)}
      >
        <Check className="h-3.5 w-3.5" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6"
        aria-label="Cancel interval edit"
        disabled={save.isPending}
        onClick={() => setEditing(false)}
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </span>
  );
};
